
import { Industry, MATT_MEDIA_FEE_PERCENTAGE } from "./constants";

type CalculationResult = {
  minEarnings: number;
  maxEarnings: number;
  periodLabel: string;
  feeAmount: {
    min: number;
    max: number;
  };
  emailFeeAmount: {
    min: number;
    max: number;
  };
  totalEarnings: {
    min: number;
    max: number;
  };
  projections: {
    day: { min: number; max: number };
    week: { min: number; max: number };
    month: { min: number; max: number };
    quarter: { min: number; max: number };
    year: { min: number; max: number };
  };
};

export function calculateEarnings(
  listSize: number,
  industry: Industry,
  frequency: number
): CalculationResult {
  // Calculate earnings per 1000 subscribers
  const minPerThousand = industry.minValue;
  const maxPerThousand = industry.maxValue;
  
  // Calculate base earnings - revenue scales with frequency
  const baseMinEarnings = (listSize / 1000) * minPerThousand * frequency;
  const baseMaxEarnings = (listSize / 1000) * maxPerThousand * frequency;
  
  // Calculate Matt Media fee
  const minFee = baseMinEarnings * (MATT_MEDIA_FEE_PERCENTAGE / 100);
  const maxFee = baseMaxEarnings * (MATT_MEDIA_FEE_PERCENTAGE / 100);
  
  // Calculate email sending fee (based on list size and frequency)
  // Assuming $0.001 per email sent (standard industry rate)
  const emailCostPerSend = 0.001 * listSize;
  const minEmailFee = emailCostPerSend * frequency; // Simply multiply by frequency (emails per week)
  const maxEmailFee = minEmailFee; // Same for min and max since it's a fixed cost
  
  // Calculate total earnings after both fees
  const totalMinEarnings = baseMinEarnings - minFee - minEmailFee;
  const totalMaxEarnings = baseMaxEarnings - maxFee - maxEmailFee;
  
  // Determine period label
  const periodLabel = "week";
  
  // Calculate projections for different time periods
  // All calculations are now based on weekly rates
  const weeklyMinRate = totalMinEarnings;
  const weeklyMaxRate = totalMaxEarnings;
  
  const projections = {
    day: {
      min: weeklyMinRate / 7,
      max: weeklyMaxRate / 7
    },
    week: {
      min: weeklyMinRate,
      max: weeklyMaxRate
    },
    month: {
      min: weeklyMinRate * 4.33, // Average weeks in a month
      max: weeklyMaxRate * 4.33
    },
    quarter: {
      min: weeklyMinRate * 13, // 13 weeks in a quarter
      max: weeklyMaxRate * 13
    },
    year: {
      min: weeklyMinRate * 52, // 52 weeks in a year
      max: weeklyMaxRate * 52
    }
  };
  
  return {
    minEarnings: baseMinEarnings,
    maxEarnings: baseMaxEarnings,
    periodLabel,
    feeAmount: {
      min: minFee,
      max: maxFee,
    },
    emailFeeAmount: {
      min: minEmailFee,
      max: maxEmailFee,
    },
    totalEarnings: {
      min: totalMinEarnings,
      max: totalMaxEarnings,
    },
    projections
  };
}

export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}
